import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Truck } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { http, errMsg } from "@/lib/api";
import { sendToShopifyCart } from "@/lib/embed";
import { clearDraft } from "@/lib/draft";
import { renderToBlob, getSize, getTemplate, printPixels, DELIVERY_DELAY, PANEL_MATERIAL, NO_SUPPORT_NOTICE, FINAL_NOTICE } from "@/lib/poster";

export const OrderDialog = ({ open, onOpenChange, cfg, photos, embed, draft, productHandle }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const phoneDigits = phone.replace(/[^0-9]/g, "");
  const validPhone = /^\+?[0-9\s().-]+$/.test(phone.trim()) && phoneDigits.length >= 8 && phoneDigits.length <= 15;
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState("");
  const [done, setDone] = useState(null);
  const cartTimer = useRef(null);
  const size = getSize(cfg.sizeId);

  useEffect(() => () => clearTimeout(cartTimer.current), []);
  useEffect(() => { if (open) toast.dismiss(); }, [open]);

  useEffect(() => { setDone(null); }, [cfg, photos, name, email, phone, productHandle]);

  useEffect(() => {
    if (!embed) return undefined;
    const onMsg = async (e) => {
      if (e.source !== window.parent || e.origin !== new URL(process.env.REACT_APP_SHOPIFY_STORE_URL).origin) return;
      if (e.data?.type === "ms-cart-added") {
        clearTimeout(cartTimer.current);
        await clearDraft(draft, Object.keys(photos));
        window.parent.postMessage({ type: "ms-cart-open" }, e.origin);
      }
      if (e.data?.type === "ms-cart-error") {
        clearTimeout(cartTimer.current);
        setBusy("");
        toast.error(e.data.message || "Impossible d'ajouter le tableau au panier Shopify. Réessayez.");
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [embed, draft, photos]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!validPhone) return toast.error("Renseignez un numéro de téléphone valide avant l’ajout au panier.");
    try {
      let data = done;
      if (!data) {
        setBusy("Génération du fichier d'impression haute définition…");
        const px = printPixels(size);
        const print = await renderToBlob(cfg, photos, { width: px.w, height: px.h });
        const preview = await renderToBlob(cfg, photos, { width: 700, height: Math.round((700 * px.h) / px.w), type: "image/jpeg", quality: 0.9 });
        setBusy("Enregistrement de votre création…");
        const fd = new FormData();
        fd.append("config", JSON.stringify({ ...cfg, shopifyProductHandle: productHandle }));
        fd.append("customer_name", embed ? "Client Shopify" : name);
        fd.append("customer_email", embed ? "" : email);
        fd.append("customer_phone", phone);
        fd.append("source", "shopify");
        fd.append("print_file", print, "print.png");
        fd.append("preview_file", preview, "preview.jpg");
        for (const k of ["groom", "bride"].slice(0, cfg.photoCount)) {
          fd.append(`cutout_${k}`, photos[k].cutoutBlob, `cutout_${k}.png`);
          fd.append(`original_${k}`, photos[k].original, photos[k].original.name || `original_${k}.jpg`);
        }
        ({ data } = await http.post("/orders", fd));
        setDone(data);
      }
      setBusy("Ouverture de votre panier Shopify…");
      if (!embed) await clearDraft(draft, Object.keys(photos));
      if (embed) {
        clearTimeout(cartTimer.current);
        cartTimer.current = setTimeout(() => {
          setBusy("");
          toast.error("Le panier Shopify ne répond pas. Votre création est enregistrée : réessayez.");
        }, 15000);
      }
      sendToShopifyCart(cfg, data, embed);
    } catch (err) {
      clearTimeout(cartTimer.current);
      toast.error(errMsg(err));
      setBusy("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className={`max-w-lg max-h-[90dvh] overflow-y-auto bg-[#F6F0EB] border-[#DDD3C6] ${embed ? "ms-embed-dialog" : ""}`} data-testid="order-dialog">
        <form onSubmit={submit} className="space-y-5">
            <DialogHeader>
              <DialogTitle className="font-display text-3xl font-normal">Ajouter mon tableau au panier</DialogTitle>
              <DialogDescription>Le tableau sera imprimé comme l'aperçu. Vous pourrez ajouter d’autres articles dans le panier Shopify avant de payer.</DialogDescription>
            </DialogHeader>
            <div className="rounded-xl bg-white border border-[#DDD3C6] p-4 text-sm space-y-2">
              <div className="flex justify-between"><span className="text-[#9A8C7D]">Modèle</span><span>{getTemplate(cfg.templateId).name}</span></div>
              <div className="flex justify-between gap-3"><span className="text-[#9A8C7D] shrink-0">Format</span><span className="text-right">{size.label} · {PANEL_MATERIAL}</span></div>
              <div className="flex justify-between"><span className="text-[#9A8C7D]">Photos</span><span>{cfg.photoMode === "bw" ? "Noir & blanc" : "Couleur"}</span></div>
              <div className="flex justify-between"><span className="text-[#9A8C7D]">Livraison</span><span className="text-[#4A5D4E] font-medium" data-testid="order-dialog-free-shipping">Offerte</span></div>
              <div className="flex justify-between font-medium text-base pt-2 border-t border-[#DDD3C6]"><span>Total</span><span data-testid="order-dialog-price">{size.price} €</span></div>
            </div>
            <div className="flex gap-2 items-center text-sm text-[#3A322B] bg-[#EADFD3] rounded-xl p-3" data-testid="order-dialog-delivery-notice">
              <Truck size={16} className="text-[#7B5338]" /> Délai de livraison : <strong>{DELIVERY_DELAY}</strong>
            </div>
            <div className="text-xs space-y-1.5 text-[#6B5F53]" data-testid="order-dialog-notes">
              <p className="text-[#7B5338] font-medium">{FINAL_NOTICE}</p>
              <p>{NO_SUPPORT_NOTICE}</p>
            </div>
            {!embed && <>
            <Input required placeholder="Prénom et nom" value={name} onChange={(e) => setName(e.target.value)} className="h-12 bg-white" data-testid="order-name-input" />
            <Input required type="email" placeholder="Adresse e-mail" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 bg-white" data-testid="order-email-input" />
            </>}
            <div className="space-y-2">
              <label htmlFor="order-phone" className="block text-sm text-[#3A322B]">Numéro de téléphone <span className="text-[#7B5338]">(obligatoire)</span></label>
              <Input id="order-phone" required type="tel" inputMode="tel" autoComplete="tel" maxLength={30} placeholder="Ex : +33 6 12 34 56 78" value={phone} onChange={(e) => setPhone(e.target.value)}
                aria-invalid={!!phone && !validPhone} aria-describedby="order-phone-help" className="h-12 bg-white" data-testid="order-phone-input" />
              <p id="order-phone-help" className={`text-xs ${phone && !validPhone ? "text-red-700" : "text-[#6B5F53]"}`} data-testid="order-phone-help">
                {phone && !validPhone ? "Saisissez entre 8 et 15 chiffres, avec l’indicatif si nécessaire." : "Requis avant l’ajout au panier pour le suivi de votre commande."}
              </p>
            </div>
            <label className="flex gap-3 items-start text-sm text-[#6B5F53] cursor-pointer">
              <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} className="mt-0.5" data-testid="order-agree-checkbox" />
              J'ai vérifié l'aperçu (textes, couleurs, photos) et je valide ce rendu pour l'impression. Je comprends que cette validation est définitive.
            </label>
            <Link to="/cgv" target="_blank" rel="noreferrer" className="inline-block text-xs text-[#7B5338] underline underline-offset-4" data-testid="order-terms-link">Lire les conditions générales de vente</Link>
            <button type="submit" disabled={!agree || !validPhone || !!busy} className="btn-gold w-full justify-center disabled:opacity-50" data-testid="order-submit-button">
              {busy ? <><Loader2 size={16} className="animate-spin" /> {busy}</> : `Ajouter au panier — ${size.price} €`}
            </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
